import { HDate, HebrewCalendar, flags, months } from '@hebcal/core';

/**
 * Calendar facts for one date (Israel), worked out from the date alone, so they
 * are the same whichever zmanim provider is used. Rules: docs/day-types-spec.md.
 * Phase 6, step 1: not used by the minyan logic yet.
 */

export type SpecificYomTov =
  | 'roshHashana'
  | 'sukkotI'
  | 'simchatTorah'
  | 'pesachI'
  | 'shviiShelPesach'
  | 'shavuot';

export type MinorFast = 'tzomGedalia' | 'asaraBTevet' | 'taanitEsther' | 'tzomTammuz';

export interface DayFacts {
  date: string;
  /** 0 = Sunday ... 6 = Shabbos. */
  weekday: number;
  shabbos: boolean;
  /** Friday. */
  erevShabbos: boolean;
  /** Monday or Thursday. */
  mondayOrThursday: boolean;
  /** Yom Tov (Yom Kippur not included). */
  yomTov: boolean;
  specificYomTov?: SpecificYomTov;
  /** 1 or 2 on Rosh Hashana. */
  roshHashanaDay?: 1 | 2;
  /** Shavuot or Shvi'i shel Pesach on a Friday. */
  yomTovOnFriday: boolean;
  /** Rosh Hashana day 2 on a Friday (its own set-up). */
  roshHashanaOnFriday: boolean;
  /** The day before Rosh Hashana, Sukkot, Shemini Atzeret, Pesach, Shvi'i shel Pesach or Shavuot. */
  erevYomTov: boolean;
  erevYomKippur: boolean;
  yomKippur: boolean;
  /** 3 to 9 Tishrei. */
  aseretYemeiTeshuva: boolean;
  cholHaMoed: boolean;
  hoshanaRabba: boolean;
  /** The last day of Chol HaMoed Pesach (20 Nisan). */
  pesachDay6: boolean;
  roshChodesh: boolean;
  /** 1 to 8, or undefined outside Chanukah. */
  chanukahDay?: number;
  purim: boolean;
  /** Observed date (moved when needed). */
  minorFast?: MinorFast;
  /** Observed date (Sunday when 9 Av is Shabbos). */
  tishaBav: boolean;
  /** The day before the observed Tisha B'Av (can be Shabbos). */
  erevTishaBav: boolean;
  /** Monday, Thursday, Monday after Rosh Chodesh Cheshvan / Iyar. */
  behab: boolean;
  /**
   * Bein Hazmanim (Yoel, October 4, 2026): after Tisha B'Av to the end of Av,
   * 1 Nisan to Erev Pesach, Isru Chag Pesach to the end of Nisan, after Yom Kippur
   * to Erev Sukkot, Isru Chag Sukkot to the end of Tishrei.
   */
  beinHazmanim: boolean;
  /** Yizkor is said (also on Shabbos). */
  yizkor: boolean;
  /** 29 Elul. */
  erevRoshHashana: boolean;
  /** 2 to 28 Elul. */
  elulBeforeErevRH: boolean;
  /** 2 Elul or later: Sefardi Selichos have started (until Erev Yom Kippur). */
  sefardiSelichotSeason: boolean;
  /** The Saturday night of the first Ashkenaz Selichos (shown on that Saturday). */
  selichotFirstNight: boolean;
  /** From the Monday after the first Selichos night until Rosh Hashana, plus Aseret Yemei Teshuva. */
  ashkenazSelichotSeason: boolean;
}

/** Parse 'YYYY-MM-DD' at noon UTC (avoids day roll-over), same as hebrewSeason.ts. */
function toHDate(dateStr: string): HDate {
  return new HDate(new Date(`${dateStr}T12:00:00Z`));
}

function toDateStr(hd: HDate): string {
  const g = hd.greg();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${g.getFullYear()}-${pad(g.getMonth() + 1)}-${pad(g.getDate())}`;
}

/** Add whole days to a 'YYYY-MM-DD' string. */
export function addDays(dateStr: string, days: number): string {
  return toDateStr(toHDate(dateStr).add(days, 'd'));
}

function holidays(hd: HDate) {
  return HebrewCalendar.getHolidaysOnDate(hd, true) ?? [];
}

function isChag(hd: HDate): boolean {
  return holidays(hd).some(
    (e) => (e.getFlags() & flags.CHAG) !== 0 && e.getDesc() !== 'Yom Kippur'
  );
}

/** Hebcal names the moved fast "Tish'a B'Av (observed)". */
function isTishaBavHd(hd: HDate): boolean {
  return holidays(hd).some((e) => e.getDesc().startsWith("Tish'a B'Av"));
}

const MINOR_FASTS: Record<string, MinorFast> = {
  'Tzom Gedaliah': 'tzomGedalia',
  "Asara B'Tevet": 'asaraBTevet',
  "Ta'anit Esther": 'taanitEsther',
  'Tzom Tammuz': 'tzomTammuz',
};

function specificYomTovOf(hd: HDate): SpecificYomTov | undefined {
  const m = hd.getMonth();
  const d = hd.getDate();
  if (m === months.TISHREI && (d === 1 || d === 2)) return 'roshHashana';
  if (m === months.TISHREI && d === 15) return 'sukkotI';
  if (m === months.TISHREI && d === 22) return 'simchatTorah';
  if (m === months.NISAN && d === 15) return 'pesachI';
  if (m === months.NISAN && d === 21) return 'shviiShelPesach';
  if (m === months.SIVAN && d === 6) return 'shavuot';
  return undefined;
}

function chanukahDayOf(hd: HDate): number | undefined {
  if (hd.getMonth() !== months.KISLEV && hd.getMonth() !== months.TEVET) return undefined;
  const first = new HDate(25, months.KISLEV, hd.getFullYear()).abs();
  const n = hd.abs() - first + 1;
  return n >= 1 && n <= 8 ? n : undefined;
}

function isPurimHd(hd: HDate): boolean {
  const adar = HDate.isLeapYear(hd.getFullYear()) ? months.ADAR_II : months.ADAR_I;
  return hd.getMonth() === adar && hd.getDate() === 14;
}

const behabCache = new Map<number, Set<number>>();

/**
 * BeHaB: the first Monday, Thursday and following Monday after the first Shabbos
 * on or after 1 Cheshvan / 1 Iyar (confirmed by Yoel: when that Shabbos is itself
 * Rosh Chodesh, BeHaB starts the Monday after it).
 */
function behabDays(year: number): Set<number> {
  const cached = behabCache.get(year);
  if (cached) return cached;
  const days = new Set<number>();
  for (const month of [months.CHESHVAN, months.IYYAR]) {
    const first = new HDate(1, month, year);
    const shabbos = first.abs() + ((6 - first.getDay() + 7) % 7);
    [2, 5, 9].forEach((offset) => days.add(shabbos + offset));
  }
  behabCache.set(year, days);
  return days;
}

/**
 * The first Ashkenaz Selichos night: the last Motzei Shabbos at least 4 days
 * before Rosh Hashana (a week earlier when Rosh Hashana is Monday or Tuesday).
 * `year` is the Hebrew year of that Elul. Returns the Saturday's abs day.
 */
function firstSelichotNightAbs(year: number): number {
  const rh = new HDate(1, months.TISHREI, year + 1).abs();
  let sat = rh - 1;
  while (new HDate(sat).getDay() !== 6 || rh - sat < 4) sat -= 1;
  return sat;
}

const factsCache = new Map<string, DayFacts>();

export function getDayFacts(dateStr: string): DayFacts {
  const cached = factsCache.get(dateStr);
  if (cached) return cached;

  const hd = toHDate(dateStr);
  const tomorrow = hd.next();
  const weekday = hd.getDay();
  const m = hd.getMonth();
  const d = hd.getDate();
  const evs = holidays(hd);
  const has = (bit: number) => evs.some((e) => (e.getFlags() & bit) !== 0);

  const yomTov = isChag(hd);
  const specificYomTov = specificYomTovOf(hd);
  const yomKippur = m === months.TISHREI && d === 10;
  const fastName = evs.map((e) => MINOR_FASTS[e.getDesc()]).find(Boolean);
  const tishaBav = isTishaBavHd(hd);

  const facts: DayFacts = {
    date: dateStr,
    weekday,
    shabbos: weekday === 6,
    erevShabbos: weekday === 5,
    mondayOrThursday: weekday === 1 || weekday === 4,
    yomTov,
    specificYomTov,
    roshHashanaDay: specificYomTov === 'roshHashana' ? (d as 1 | 2) : undefined,
    yomTovOnFriday:
      weekday === 5 && (specificYomTov === 'shavuot' || specificYomTov === 'shviiShelPesach'),
    roshHashanaOnFriday: weekday === 5 && specificYomTov === 'roshHashana' && d === 2,
    erevYomTov: !yomTov && isChag(tomorrow),
    erevYomKippur: m === months.TISHREI && d === 9,
    yomKippur,
    aseretYemeiTeshuva: m === months.TISHREI && d >= 3 && d <= 9,
    cholHaMoed: has(flags.CHOL_HAMOED),
    hoshanaRabba: m === months.TISHREI && d === 21,
    pesachDay6: m === months.NISAN && d === 20,
    roshChodesh: has(flags.ROSH_CHODESH),
    chanukahDay: chanukahDayOf(hd),
    purim: isPurimHd(hd),
    minorFast: fastName,
    tishaBav,
    erevTishaBav: !tishaBav && isTishaBavHd(tomorrow),
    beinHazmanim:
      (m === months.AV && d > (new HDate(9, months.AV, hd.getFullYear()).getDay() === 6 ? 10 : 9) && d <= 29) ||
      (m === months.NISAN && ((d >= 1 && d <= 14) || (d >= 22 && d <= 29))) ||
      (m === months.TISHREI && ((d >= 11 && d <= 14) || (d >= 23 && d <= 29))),
    behab: (m === months.CHESHVAN || m === months.IYYAR) && behabDays(hd.getFullYear()).has(hd.abs()),
    yizkor:
      yomKippur || specificYomTov === 'simchatTorah' || specificYomTov === 'shviiShelPesach' ||
      specificYomTov === 'shavuot',
    erevRoshHashana: m === months.ELUL && d === 29,
    elulBeforeErevRH: m === months.ELUL && d >= 2 && d <= 28,
    sefardiSelichotSeason: (m === months.ELUL && d >= 2) || (m === months.TISHREI && d >= 3 && d <= 9),
    selichotFirstNight: m === months.ELUL && hd.abs() === firstSelichotNightAbs(hd.getFullYear()),
    ashkenazSelichotSeason:
      (m === months.ELUL && hd.abs() >= firstSelichotNightAbs(hd.getFullYear()) + 2) ||
      (m === months.TISHREI && d >= 3 && d <= 9),
  };
  factsCache.set(dateStr, facts);
  return facts;
}

/**
 * Whether the evening of this date belongs to the next day (spec section 3):
 * yes when the next day is Shabbos, Yom Tov, Yom Kippur, Tisha B'Av or Purim.
 * Otherwise the evening's Maariv stays with this date (a weekday Maariv, the
 * Maariv after Shabbos or after Yom Tov).
 */
export function eveningBelongsToNextDay(dateStr: string): boolean {
  const next = getDayFacts(addDays(dateStr, 1));
  return next.shabbos || next.yomTov || next.yomKippur || next.tishaBav || next.purim;
}
